/**
 * Billing carve adjustments.
 *
 * A line's TARGET BILLING is its share of its term bucket's life-to-date
 * billing, the share being its cumulative allocated revenue over the bucket's
 * total; target minus the line's own cumulative billing is the CUMULATIVE CARVE
 * ADJUSTMENT. Every share comes from the same bucket-level pool, so the carve
 * nets to zero within the bucket.
 *
 * Pure: no I/O, no org config, nothing mutated. Every run recomputes from the
 * caller's snapshot plus the clamped opening balance.
 *
 * CUMULATIVES ONLY. The period movement is derived in exactly one place, the
 * rollforward's `Ending - Beginning` update into
 * `Unearned_.Billing_Adjustment`.
 */
import Big from "big.js";

import {
    CARVE_AMOUNT_SCALE,
    CARVE_MAX_ALLOCATION_SHARE,
    CARVE_PERCENT_SCALE
} from "./billingCarveConstants";

/**
 * The eight columns the rollforward joins a carve row to the spine on, copied
 * field for field from the spine -- that is, from what `exportSalesOrderItems`
 * publishes to `SalesOrders_`, not from the billing schedule.
 *
 * `salesOrderId` is the SYNTHETIC `IN-<customerId>` form -- customer grain --
 * while `linkedSalesOrderId` holds the real sales order. Conflating them yields
 * a table that joins perfectly and nets across the customer's entire book.
 */
export interface CarveJoinKey {
    salesOrderId: string;
    linkedSalesOrderId: string;
    referenceInvoiceNumber: string;
    subscriptionId: string;
    invoiceNumber: string;
    salesOrderItemId: string;
    productId: string;
    customerId: string;
}

export interface CarveLine {
    salesOrderItemId: string;
    linkedSalesOrderId: string;
    termBucket: number;

    /**
     * The one exclusion test. An item with the flag set contributes neither
     * billing nor allocated revenue to its bucket, and receives no carve.
     */
    skipAllocation: boolean;

    /** Booked revenue for frozen lines, post-modification allocation for active ones. */
    cumulativeAllocatedRevenue: number;

    /**
     * Cumulative -- not per-period -- billing, keyed by accounting period.
     * Sparse: a period with no entry carries the last known value forward.
     * Produced by `utils/DataExportHelper` `getCumulativeBillingByLine`, which
     * supplies amounts only -- never any part of `joinKey`.
     */
    cumulativeBillingByPeriod: Map<string, number>;

    joinKey: CarveJoinKey;

    /**
     * The bucket the contract id behind this line's post-modification allocated
     * revenue maps to. Supplied only when the caller can resolve it. A value
     * disagreeing with `termBucket` means a renewal reused an existing line, so
     * the line's allocated revenue straddles two terms and one sticky bucket
     * cannot represent it.
     */
    postModificationTermBucket?: number;
}

export interface CarveInput {
    /** May span several orders; pools are formed per order and bucket. */
    lines: CarveLine[];

    /** First open accounting period. Nothing at or before this is emitted. */
    openPeriod: string;

    /** Periods to compute, ascending. Periods before `openPeriod` are read, not emitted. */
    periods: string[];

    /**
     * Cumulative carve as at the last closed period, keyed by `carveLineKey`.
     * Read back, never recomputed -- see `workers/processSalesOrder`
     * `loadCarveOpeningBalances`.
     */
    openingBalances: Map<string, number>;

    tolerance: (lineCount: number) => number;
}

/** A `BillingCarveAdjustment_` row. Transactional and cumulative only. */
export interface CarveRow extends CarveJoinKey {
    actgPeriod: string;

    /** Carried for audit; not part of the rollforward join. */
    termBucket: number;

    cumulativeAllocatedRevenue: number;

    /** A fraction, not a percentage. `DECIMAL(14,8)`. */
    cumulativeAllocationPercent: number;

    cumulativeBilling: number;
    targetBilling: number;
    cumulativeCarve: number;
}

export interface CarveNettingFailure {
    termBucket: number;
    actgPeriod: string;
    residual: number;
    tolerance: number;
    lineCount: number;
}

export interface CarvePreconditionBreach {
    salesOrderItemId: string;
    stampedTermBucket: number;
    postModificationTermBucket: number;
}

/**
 * One warning per order per run. `JobOutputErrors` holds a single row per order
 * under REPLACE semantics, so a second code on the same order would silently
 * replace or be replaced by the first. Netting failures and precondition
 * breaches therefore share one code.
 */
export interface CarveWarning {
    linkedSalesOrderId: string;
    nettingFailures: CarveNettingFailure[];
    preconditionBreaches: CarvePreconditionBreach[];
    specificMessage: string;
}

export interface CarveResult {
    rows: CarveRow[];
    warnings: CarveWarning[];
}

export function carveLineKey(
    linkedSalesOrderId: string,
    termBucket: number,
    salesOrderItemId: string
): string {
    return `${linkedSalesOrderId}|${termBucket}|${salesOrderItemId}`;
}

const poolKey = (linkedSalesOrderId: string, termBucket: number) =>
    `${linkedSalesOrderId}|${termBucket}`;

const ZERO = new Big(0);

const toNumber = (value: Big, scale: number) =>
    Number(value.round(scale, Big.roundHalfUp));

interface Pool {
    linkedSalesOrderId: string;
    termBucket: number;
    lines: CarveLine[];
    totalAllocatedRevenue: Big;
}

const buildPools = (lines: CarveLine[]): Pool[] => {
    const byKey = new Map<string, Pool>();

    for (const line of lines) {
        // Both sides of the exclusion move together -- see `CarveLine`.
        if (line.skipAllocation) continue;

        const key = poolKey(line.linkedSalesOrderId, line.termBucket);
        let pool = byKey.get(key);
        if (!pool) {
            pool = {
                linkedSalesOrderId: line.linkedSalesOrderId,
                termBucket: line.termBucket,
                lines: [],
                totalAllocatedRevenue: ZERO
            };
            byKey.set(key, pool);
        }

        pool.lines.push(line);
        pool.totalAllocatedRevenue = pool.totalAllocatedRevenue.plus(
            new Big(line.cumulativeAllocatedRevenue)
        );
    }

    return Array.from(byKey.values());
};

/**
 * Cumulative billing for every line of a pool at every period, densified by
 * carrying the last known value forward.
 *
 * Keyed by line identity, not `salesOrderItemId` -- only `priorCumulativeCarve`
 * assumes that id is unique within a pool. `cumulativeBillingByPeriod` is a
 * public interface, so its keys are sorted here rather than trusted in order.
 */
const denseBillingForPool = (
    pool: Pool,
    periods: string[]
): Map<CarveLine, Map<string, Big>> => {
    const byLine = new Map<CarveLine, Map<string, Big>>();

    for (const line of pool.lines) {
        const entries = Array.from(
            line.cumulativeBillingByPeriod.entries()
        ).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));

        const dense = new Map<string, Big>();
        let i = 0;
        let carried = ZERO;

        for (const period of periods) {
            while (i < entries.length && entries[i][0] <= period) {
                carried = new Big(entries[i][1]);
                i += 1;
            }
            dense.set(period, carried);
        }

        byLine.set(line, dense);
    }

    return byLine;
};

const appendTo = <T>(map: Map<string, T[]>, key: string, value: T) => {
    const existing = map.get(key);
    if (existing) {
        existing.push(value);
        return;
    }
    map.set(key, [value]);
};

const describeWarning = (
    nettingFailures: CarveNettingFailure[],
    preconditionBreaches: CarvePreconditionBreach[]
): string => {
    const parts: string[] = [];

    for (const failure of nettingFailures) {
        parts.push(
            `Term bucket ${failure.termBucket} period ${failure.actgPeriod}: ` +
                `carve nets to ${failure.residual.toFixed(2)} across ` +
                `${failure.lineCount} line(s), outside a tolerance of ` +
                `${failure.tolerance.toFixed(2)}.`
        );
    }

    for (const breach of preconditionBreaches) {
        parts.push(
            `Order item ${breach.salesOrderItemId} is stamped term bucket ` +
                `${breach.stampedTermBucket} but its post-modification ` +
                `allocated revenue arises under a contract mapping to term ` +
                `bucket ${breach.postModificationTermBucket}; its allocated ` +
                `revenue straddles two terms.`
        );
    }

    return parts.join(" ");
};

export function computeCarveAdjustments(input: CarveInput): CarveResult {
    const { lines, openPeriod, openingBalances, tolerance } = input;

    const periods = [...input.periods].sort();
    const pools = buildPools(lines);

    const rows: CarveRow[] = [];
    const nettingFailuresByOrder = new Map<string, CarveNettingFailure[]>();
    const breachesByOrder = new Map<string, CarvePreconditionBreach[]>();

    for (const line of lines) {
        if (
            line.postModificationTermBucket === undefined ||
            line.postModificationTermBucket === line.termBucket
        ) {
            continue;
        }

        appendTo(breachesByOrder, line.linkedSalesOrderId, {
            salesOrderItemId: line.salesOrderItemId,
            stampedTermBucket: line.termBucket,
            postModificationTermBucket: line.postModificationTermBucket
        });
    }

    for (const pool of pools) {
        if (pool.totalAllocatedRevenue.eq(ZERO)) continue;

        // A pool whose lines nearly cancel has a near-zero denominator and
        // shares in the thousands, so its target billings are nonsense. The
        // share is time-invariant, so the skip covers the whole pool. Must
        // follow the zero check: `Big.js` throws on division by zero.
        //
        // A skip is not an absence of effect. `BillingCarveAdjustment_` is an
        // active-link table, so `cleanupOpenPeriodData` has already deleted
        // every open-period row for every order the run recomputes -- which
        // includes this one -- and a skipped pool is not rewritten.
        // `Fill_Group` then forward-fills the last surviving value, freezing
        // the pool's carve at its last closed-period cumulative across every
        // open period -- zero if it has no closed history -- which reaches the
        // journals through `Unearned_.Carve_Adjustment`. Silent by design: no
        // CARVE_01 row.
        const exceedsShareBound = pool.lines.some((line) =>
            new Big(line.cumulativeAllocatedRevenue)
                .div(pool.totalAllocatedRevenue)
                .abs()
                .gt(CARVE_MAX_ALLOCATION_SHARE)
        );
        if (exceedsShareBound) continue;

        const denseBilling = denseBillingForPool(pool, periods);

        const priorCumulativeCarve = new Map<string, Big>();
        for (const line of pool.lines) {
            const key = carveLineKey(
                line.linkedSalesOrderId,
                line.termBucket,
                line.salesOrderItemId
            );
            priorCumulativeCarve.set(
                line.salesOrderItemId,
                new Big(openingBalances.get(key) || 0)
            );
        }

        for (const period of periods) {
            const poolCumulativeBilling = pool.lines.reduce(
                (total, line) =>
                    total.plus(denseBilling.get(line)?.get(period) as Big),
                ZERO
            );

            let residual = ZERO;

            for (const line of pool.lines) {
                const allocationPercent = new Big(
                    line.cumulativeAllocatedRevenue
                ).div(pool.totalAllocatedRevenue);
                const lineCumulativeBilling = denseBilling
                    .get(line)
                    ?.get(period) as Big;

                // Rounded independently per line -- the dust the tolerance in
                // `billingCarveConstants` is calibrated against.
                const targetBilling = allocationPercent
                    .times(poolCumulativeBilling)
                    .round(CARVE_AMOUNT_SCALE, Big.roundHalfUp);
                const cumulativeCarve = targetBilling.minus(
                    lineCumulativeBilling
                );

                residual = residual.plus(cumulativeCarve);

                // Closed periods are clamped: the last closed cumulative is an
                // immutable opening balance, so it is read but never restated.
                if (period < openPeriod) continue;

                const prior =
                    priorCumulativeCarve.get(line.salesOrderItemId) || ZERO;
                priorCumulativeCarve.set(
                    line.salesOrderItemId,
                    cumulativeCarve
                );

                // Sparse: a row only where the recomputed cumulative moves. A
                // dormant bucket recomputes to the value it already holds and
                // emits nothing, and Fill_Group holds it flat.
                if (cumulativeCarve.eq(prior)) continue;

                rows.push({
                    ...line.joinKey,
                    actgPeriod: period,
                    termBucket: line.termBucket,
                    cumulativeAllocatedRevenue: toNumber(
                        new Big(line.cumulativeAllocatedRevenue),
                        CARVE_AMOUNT_SCALE
                    ),
                    cumulativeAllocationPercent: toNumber(
                        allocationPercent,
                        CARVE_PERCENT_SCALE
                    ),
                    cumulativeBilling: toNumber(
                        lineCumulativeBilling,
                        CARVE_AMOUNT_SCALE
                    ),
                    targetBilling: toNumber(targetBilling, CARVE_AMOUNT_SCALE),
                    cumulativeCarve: toNumber(
                        cumulativeCarve,
                        CARVE_AMOUNT_SCALE
                    )
                });
            }

            if (period < openPeriod) continue;

            // Post-validation: the rows are published either way, so a netting
            // failure stays visible to the reconciliation.
            const allowed = tolerance(pool.lines.length);
            if (residual.abs().gt(new Big(allowed))) {
                appendTo(nettingFailuresByOrder, pool.linkedSalesOrderId, {
                    termBucket: pool.termBucket,
                    actgPeriod: period,
                    residual: toNumber(residual, CARVE_AMOUNT_SCALE),
                    tolerance: allowed,
                    lineCount: pool.lines.length
                });
            }
        }
    }

    const warnings: CarveWarning[] = [];
    const orderIds = new Set([
        ...nettingFailuresByOrder.keys(),
        ...breachesByOrder.keys()
    ]);

    for (const linkedSalesOrderId of orderIds) {
        const nettingFailures =
            nettingFailuresByOrder.get(linkedSalesOrderId) || [];
        const preconditionBreaches =
            breachesByOrder.get(linkedSalesOrderId) || [];

        warnings.push({
            linkedSalesOrderId,
            nettingFailures,
            preconditionBreaches,
            specificMessage: describeWarning(
                nettingFailures,
                preconditionBreaches
            )
        });
    }

    return { rows, warnings };
}
